import React, { useState } from "react";
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
} from "@tanstack/react-table";
import "./ReleasesTable.css";

/* ─── Helpers ──────────────────────────────────────────────── */

function formatDateTime(isoString) {
    if (!isoString) return "—";
    return new Date(isoString).toLocaleDateString("en-IN", {
        year: "numeric", month: "short", day: "numeric",
    });
}

function StatusBadge({ status }) {
    const s = (status || "").toLowerCase();
    const cls =
        s === "released" ? "rt-status rt-status--released"
            : s === "ready" ? "rt-status rt-status--ready"
                : "rt-status rt-status--draft";
    return <span className={cls}>{status || "—"}</span>;
}

function SortIcon({ state }) {
    if (state === "asc") return (
        <svg className="rt-sort-icon rt-sort-icon--active" width="14" height="14"
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m18 15-6-6-6 6" />
        </svg>
    );
    if (state === "desc") return (
        <svg className="rt-sort-icon rt-sort-icon--active" width="14" height="14"
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
        </svg>
    );
    return (
        <svg className="rt-sort-icon" width="14" height="14"
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m8 9 4-4 4 4M16 15l-4 4-4-4" />
        </svg>
    );
}

function ChevronRightIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round"
            className="rt-row-chevron" aria-hidden="true">
            <path d="m9 18 6-6-6-6" />
        </svg>
    );
}

/* ─── Skeleton row ─────────────────────────────────────────── */

function SkeletonRows({ count = 5 }) {
    return Array.from({ length: count }).map((_, i) => (
        <tr key={`sk-${i}`} className="rt-skeleton-row" aria-hidden="true">
            <td className="rt-td" style={{ width: "30%" }}>
                <div className="rt-skeleton" style={{ width: "75%", height: 14 }} />
                <div className="rt-skeleton" style={{ width: "50%", height: 11, marginTop: 6 }} />
            </td>
            <td className="rt-td">
                <div className="rt-skeleton rt-skeleton--badge" style={{ width: 64, height: 24 }} />
            </td>
            <td className="rt-td">
                <div className="rt-skeleton" style={{ width: "70%", height: 14 }} />
            </td>
            <td className="rt-td">
                <div className="rt-skeleton" style={{ width: "80%", height: 14 }} />
            </td>
            <td className="rt-td">
                <div className="rt-skeleton" style={{ width: "80%", height: 14 }} />
            </td>
            <td className="rt-td" style={{ width: 40 }}>
                <div className="rt-skeleton" style={{ width: 16, height: 16, borderRadius: "50%" }} />
            </td>
        </tr>
    ));
}

/* ─── Main component ───────────────────────────────────────── */

/**
 * ReleasesTable
 * @param {object[]} data       - releases from API 1
 * @param {function} onRowClick - called with release object on row click
 * @param {boolean}  loading    - show skeleton rows instead of data
 */
const ReleasesTable = ({ data = [], onRowClick, loading = false }) => {
    const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);

    const columns = [
        {
            id: "releaseName",
            accessorKey: "releaseName",
            header: "Release Name",
            cell: ({ getValue, row }) => {
                const rid = row.original.releaseId;
                const shortId = rid
                    ? rid.length > 8 ? `${rid.slice(0, 8)}…` : rid
                    : null;
                return (
                    <div className="rt-cell-name">
                        <span className="rt-name-label">{getValue() || "—"}</span>
                        <span className="rt-name-id">{shortId || "—"}</span>
                    </div>
                );
            },
            enableSorting: true,
        },
        {
            id: "status",
            accessorKey: "status",
            header: "Status",
            cell: ({ getValue }) => <StatusBadge status={getValue()} />,
            enableSorting: true,
        },
        {
            id: "createdBy",
            accessorKey: "createdBy",
            header: "Created By",
            cell: ({ getValue }) => (
                <div className="rt-cell-text rt-cell-text--truncate">{getValue() || "—"}</div>
            ),
            enableSorting: true,
        },
        {
            id: "createdAt",
            accessorKey: "createdAt",
            header: "Created",
            cell: ({ getValue }) => (
                <div className="rt-cell-date">{formatDateTime(getValue())}</div>
            ),
            enableSorting: true,
            sortingFn: (rowA, rowB, columnId) => {
                const a = new Date(rowA.getValue(columnId) || 0).getTime();
                const b = new Date(rowB.getValue(columnId) || 0).getTime();
                return a - b;
            },
        },
        {
            id: "updatedAt",
            accessorKey: "updatedAt",
            header: "Updated",
            cell: ({ getValue }) => (
                <div className="rt-cell-date">{formatDateTime(getValue())}</div>
            ),
            enableSorting: true,
            sortingFn: (rowA, rowB, columnId) => {
                const a = new Date(rowA.getValue(columnId) || 0).getTime();
                const b = new Date(rowB.getValue(columnId) || 0).getTime();
                return a - b;
            },
        },
        {
            id: "chevron",
            header: "",
            cell: () => <ChevronRightIcon />,
            enableSorting: false,
            size: 40,
        },
    ];

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: setSorting,
        state: { sorting },
        enableSortingRemoval: false,
    });

    /* Empty state */
    if (!loading && (!data || data.length === 0)) {
        return (
            <div className="rt-wrapper">
                <div className="rt-container">
                    <div className="rt-empty">
                        <div className="rt-empty__icon" aria-hidden="true">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="1.5"
                                strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="3" width="20" height="14" rx="2" />
                                <path d="M8 21h8M12 17v4" />
                            </svg>
                        </div>
                        <p className="rt-empty__title">No releases found</p>
                        <p className="rt-empty__sub">
                            Create your first release or adjust the search filters.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="rt-wrapper">
            <div className="rt-container">
                <table className="rt-table" role="table">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id} className="rt-head-row">
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className="rt-th"
                                        aria-sort={
                                            header.column.getIsSorted() === "asc" ? "ascending"
                                                : header.column.getIsSorted() === "desc" ? "descending"
                                                    : "none"
                                        }
                                    >
                                        <div className="rt-th-inner">
                                            <span className="rt-th-label">
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </span>
                                            {header.column.getCanSort() && (
                                                <button
                                                    type="button"
                                                    className="rt-sort-btn"
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    aria-label="Sort"
                                                >
                                                    <SortIcon state={header.column.getIsSorted()} />
                                                </button>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {loading ? (
                            <SkeletonRows count={5} />
                        ) : (
                            table.getRowModel().rows.map((row) => (
                                <tr
                                    key={row.id}
                                    className="rt-row"
                                    onClick={() => onRowClick && onRowClick(row.original)}
                                    tabIndex={0}
                                    role="button"
                                    aria-label={`Open release: ${row.original?.releaseName ?? "unknown release"}`}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            onRowClick && onRowClick(row.original);
                                        }
                                    }}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="rt-td">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ReleasesTable;
